import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { setAlert } from '../../actions/alert';
import { listarVentas, listarMetodosVenta, cancelarVenta, generarPdfVenta } from '../../actions/ventas';

import FiltrosVentasModal, { OPCIONES_STATUS_PAGO } from './modals/FiltrosVentasModal';
import CancelarVentaModal from './modals/CancelarVentaModal';

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
};

/* Status de pago por venta: 0 = No pagada | 1 = Parcial | 2 = Pagada.
   Requiere que el backend agregue este cálculo al listado (ver nota al final de la conversación). */
const renderBadgeStatusPago = (status) => {
    const s = Number(status);
    if (s === 0) return <span className="badge bg-danger">No pagada</span>;
    if (s === 1) return <span className="badge text-dark" style={{ backgroundColor: '#fd7e14' }}>Parcial</span>;
    if (s === 2) return <span className="badge bg-success">Pagada</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

export default function Ventas() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const ventas = useSelector((state) => state.ventas?.listadoVentas || []);
    const metodos = useSelector((state) => state.ventas?.metodos || []);

    const [tabTipo, setTabTipo] = useState(0);

    const [dtSearch, setDtSearch] = useState('');
    const [showFiltros, setShowFiltros] = useState(false);
    const [filtrosActivos, setFiltrosActivos] = useState(false);

    const [filtroFolio, setFiltroFolio] = useState('');
    const [filtroMetodo, setFiltroMetodo] = useState(null);
    const [filtroFechaInicio, setFiltroFechaInicio] = useState(null);
    const [filtroFechaFin, setFiltroFechaFin] = useState(null);
    const [filtroStatusPago, setFiltroStatusPago] = useState(OPCIONES_STATUS_PAGO);

    const [showCancelar, setShowCancelar] = useState(false);
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);

    const cargarListado = async ({ folio = null, metodo = null, fecha_inicio = null, fecha_fin = null, tipo = 0 } = {}) => {
        await dispatch(listarVentas({ folio, metodo, fecha_inicio, fecha_fin, tipo }));
    };

    useEffect(() => {
        dispatch(
            editarBreadcrumb([
                { nombre: 'Ventas', url: '/ventas' }
            ])
        );

        dispatch(listarMetodosVenta());
        cargarListado({ tipo: 0 });
    }, []);

    const onCambiarTab = async (nuevoTipo) => {
        if (nuevoTipo === tabTipo) return;
        setTabTipo(nuevoTipo);
        setFiltroFolio('');
        setFiltroMetodo(null);
        setFiltroFechaInicio(null);
        setFiltroFechaFin(null);
        setFiltroStatusPago(OPCIONES_STATUS_PAGO);
        setFiltrosActivos(false);
        setDtSearch('');
        await cargarListado({ tipo: nuevoTipo });
    };

    const abrirFiltros = () => {
        setShowFiltros(true);
    };

    const cancelarFiltros = () => {
        setFiltroFolio('');
        setFiltroMetodo(null);
        setFiltroFechaInicio(null);
        setFiltroFechaFin(null);
        setFiltroStatusPago(OPCIONES_STATUS_PAGO);
        setShowFiltros(false);
    };

    const buscarConFiltros = async () => {
        const folio = (filtroFolio || '').trim() || null;
        const metodo = filtroMetodo?.value || null;

        const fecha_inicio = filtroFechaInicio ? filtroFechaInicio.toISOString() : null;
        const fecha_fin = filtroFechaFin ? filtroFechaFin.toISOString() : null;

        const statusPagoEsDefault = (filtroStatusPago || []).length === OPCIONES_STATUS_PAGO.length;
        const any = !!folio || !!metodo || !!fecha_inicio || !!fecha_fin || !statusPagoEsDefault;

        await cargarListado({ folio, metodo, fecha_inicio, fecha_fin, tipo: tabTipo });

        setFiltrosActivos(any);
        setShowFiltros(false);
    };

    const limpiarFiltros = async () => {
        setFiltroFolio('');
        setFiltroMetodo(null);
        setFiltroFechaInicio(null);
        setFiltroFechaFin(null);
        setFiltroStatusPago(OPCIONES_STATUS_PAGO);
        setFiltrosActivos(false);
        await cargarListado({ tipo: tabTipo });
    };

    const onClickEliminar = (venta) => {
        setVentaSeleccionada(venta || null);
        setShowCancelar(true);
    };

    const cerrarCancelar = () => {
        setShowCancelar(false);
        setVentaSeleccionada(null);
    };

    const confirmarCancelar = async () => {
        const id = ventaSeleccionada?._id || null;

        if (!id) {
            dispatch(setAlert('No se pudo identificar la venta', 'danger'));
            cerrarCancelar();
            return;
        }

        const ok = await dispatch(cancelarVenta(id));
        cerrarCancelar();

        if (!ok) {
            return;
        }

        const folio = (filtroFolio || '').trim() || null;
        const metodo = filtroMetodo?.value || null;
        const fecha_inicio = filtroFechaInicio ? filtroFechaInicio.toISOString() : null;
        const fecha_fin = filtroFechaFin ? filtroFechaFin.toISOString() : null;

        const any = !!folio || !!metodo || !!fecha_inicio || !!fecha_fin;

        if (any) {
            await cargarListado({ folio, metodo, fecha_inicio, fecha_fin, tipo: tabTipo });
        } else {
            await cargarListado({ tipo: tabTipo });
        }
    };

    const rows = useMemo(() => {
        const arr = Array.isArray(ventas) ? ventas : [];
        return arr.map((v) => {
            const cliente = v?.cliente?.nombre || '';
            const metodo = v?.metodo?.nombre || '';
            const fechaIso = v?.fecha || '';
            const fechaText = formatDate(fechaIso);
            const fechaValue = fechaIso ? new Date(fechaIso).getTime() : 0;
            const totalValue = Number(v?.total || 0);
            const monedaValue = Number(v?.moneda) === 1 ? 1 : 0;
            const monedaText = monedaValue === 1 ? 'USD' : 'MXN';

            return {
                id: v?._id || '',
                folio: v?.folio || '',
                fechaText,
                fechaValue,
                cliente,
                totalText: `$${formatMoney(totalValue)}`,
                totalValue,
                monedaValue,
                monedaText,
                metodo,
                statusPago: v?.status_pago,
                status: !!v?.status,
                venta: v
            };
        });
    }, [ventas]);

    const filteredRows = useMemo(() => {
        let base = rows;

        const statusPagoSeleccionados = (filtroStatusPago || []).map((o) => o.value);
        if (statusPagoSeleccionados.length < OPCIONES_STATUS_PAGO.length) {
            base = base.filter((r) => statusPagoSeleccionados.includes(Number(r.statusPago)));
        }

        const q = String(dtSearch || '').trim().toLowerCase();
        if (!q) return base;

        const statusPagoTexto = { 0: 'no pagada', 1: 'parcial', 2: 'pagada' };

        return base.filter((r) => {
            const statusText = r.status ? 'activa' : 'cancelada';
            const haystack = [
                r.folio,
                r.fechaText,
                r.cliente,
                r.metodo,
                r.totalText,
                r.monedaText,
                statusText,
                statusPagoTexto[r.statusPago] || ''
            ].join(' ').toLowerCase();

            return haystack.includes(q);
        });
    }, [rows, dtSearch, filtroStatusPago]);

    const columns = useMemo(() => ([
        {
            name: 'Folio',
            selector: (row) => row.folio,
            sortable: true,
            grow: 1
        },
        {
            name: 'Fecha',
            selector: (row) => row.fechaValue,
            sortable: true,
            cell: (row) => row.fechaText,
            grow: 1
        },
        {
            name: 'Cliente',
            selector: (row) => row.cliente || '',
            sortable: true,
            cell: (row) => (row.cliente ? row.cliente : '-'),
            grow: 2
        },
        {
            name: 'Total',
            selector: (row) => row.totalValue,
            sortable: true,
            right: true,
            cell: (row) => (
                <div style={{ width: '100%', textAlign: 'right' }}>
                    {row.totalText}
                </div>
            ),
            grow: 1
        },
        {
            name: 'Moneda',
            selector: (row) => row.monedaText,
            sortable: true,
            center: true,
            cell: (row) => (
                <span className={`badge ${row.monedaValue === 1 ? 'bg-info text-dark' : 'bg-light text-dark border'}`}>
                    {row.monedaText}
                </span>
            ),
            grow: 1
        },
        {
            name: 'Método',
            selector: (row) => row.metodo || '',
            sortable: true,
            cell: (row) => (row.metodo ? row.metodo : '-'),
            grow: 1,
            omit: tabTipo === 1
        },
        {
            name: 'Pago',
            selector: (row) => row.statusPago ?? -1,
            sortable: true,
            center: true,
            cell: (row) => renderBadgeStatusPago(row.statusPago),
            grow: 1,
            omit: tabTipo === 1
        },
        {
            name: 'Estatus',
            selector: (row) => (row.status ? 1 : 0),
            sortable: true,
            center: true,
            cell: (row) => (
                row.status ? (
                    <span className="ventas-status-badge ventas-status-activa">Activa</span>
                ) : (
                    <span className="ventas-status-badge ventas-status-cancelada">Cancelada</span>
                )
            ),
            grow: 1
        },
        {
            name: 'Opciones',
            sortable: false,
            center: true,
            width: '160px',
            cell: (row) => (
                <div className="d-flex justify-content-center align-items-center">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Generar PDF"
                        onClick={() => dispatch(generarPdfVenta(row.venta._id))}
                    >
                        <i className="fas fa-file-pdf text-primary" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Ver detalle"
                        onClick={() => navigate(`/ventas/detalle/${row.venta._id}`)}
                    >
                        <i className="fas fa-eye text-primary" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Editar"
                        onClick={() => navigate(`/ventas/editar/${row.venta._id}`)}
                        disabled={!row.status}
                        style={{ opacity: row.status ? 1 : 0.45 }}
                    >
                        <i className="fas fa-edit text-warning" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Cancelar"
                        onClick={() => onClickEliminar(row.venta)}
                        disabled={!row.status}
                        style={{ opacity: row.status ? 1 : 0.45 }}
                    >
                        <i className="fas fa-trash text-danger" />
                    </button>
                </div>
            )
        }
    ]), [tabTipo, onClickEliminar]);

    const customStyles = useMemo(() => ({
        table: {
            style: {
                width: '100%'
            }
        },
        headCells: {
            style: {
                fontWeight: 600,
                fontSize: '12px',
                color: '#1b1b1b',
                paddingTop: '14px',
                paddingBottom: '14px',
                paddingLeft: '10px',
                paddingRight: '10px',
                borderBottom: '1px solid rgba(0,0,0,.08)'
            }
        },
        cells: {
            style: {
                fontSize: '13px',
                paddingTop: '14px',
                paddingBottom: '14px',
                paddingLeft: '10px',
                paddingRight: '10px',
                borderBottom: '1px solid rgba(0,0,0,.08)'
            }
        },
        pagination: {
            style: {
                borderTop: '0'
            }
        }
    }), []);

    const paginationOptions = useMemo(() => ({
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    }), []);

    return (
        <Fragment>
            <style>{`
                .ventas-toolbar input {
                    height: 38px;
                    border-radius: 6px;
                }

                .ventas-toolbar .btn {
                    height: 38px;
                    border-radius: 6px;
                    padding: 0 14px;
                    font-weight: 600;
                }

                .ventas-status-badge {
                    display: inline-block;
                    padding: 4px 10px;
                    border-radius: 999px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: .2px;
                }

                .ventas-status-activa {
                    background: rgba(25, 135, 84, .15);
                    color: #198754;
                }

                .ventas-status-cancelada {
                    background: rgba(220, 53, 69, .15);
                    color: #dc3545;
                }
            `}</style>

            <div className="row">
                <div className="col-10 offset-1">
                    <h1 className="mb-4">Ventas</h1>

                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link${tabTipo === 0 ? ' active' : ''}`}
                                onClick={() => onCambiarTab(0)}
                            >
                                Ventas
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link${tabTipo === 1 ? ' active' : ''}`}
                                onClick={() => onCambiarTab(1)}
                            >
                                Cotizaciones
                            </button>
                        </li>
                    </ul>

                    <div className="d-flex justify-content-end align-items-center gap-2 mb-3 ventas-toolbar">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar en la tabla..."
                            value={dtSearch}
                            onChange={(e) => setDtSearch(e.target.value)}
                            style={{ maxWidth: 280 }}
                        />

                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={filtrosActivos ? limpiarFiltros : abrirFiltros}
                        >
                            {filtrosActivos ? 'Limpiar filtros' : 'Filtros avanzados'}
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => navigate('/ventas/nueva')}
                        >
                            Nueva venta
                        </button>
                    </div>

                    <div className="table-responsive">
                        <DataTable
                            columns={columns}
                            data={filteredRows}
                            customStyles={customStyles}
                            pagination
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover
                            persistTableHead
                            responsive
                            noDataComponent={tabTipo === 1 ? 'Sin cotizaciones para mostrar' : 'Sin ventas para mostrar'}
                            defaultSortFieldId={2}
                            defaultSortAsc={false}
                        />
                    </div>

                    <FiltrosVentasModal
                        show={showFiltros}
                        onCancel={cancelarFiltros}
                        onBuscar={buscarConFiltros}
                        metodos={metodos}
                        folio={filtroFolio}
                        setFolio={setFiltroFolio}
                        metodo={filtroMetodo}
                        setMetodo={setFiltroMetodo}
                        fechaInicio={filtroFechaInicio}
                        setFechaInicio={setFiltroFechaInicio}
                        fechaFin={filtroFechaFin}
                        setFechaFin={setFiltroFechaFin}
                        statusPago={filtroStatusPago}
                        setStatusPago={setFiltroStatusPago}
                        mostrarStatusPago={tabTipo === 0}
                    />

                    <CancelarVentaModal
                        show={showCancelar}
                        onCancel={cerrarCancelar}
                        onConfirm={confirmarCancelar}
                        venta={ventaSeleccionada}
                    />

                </div>
            </div>
        </Fragment>
    );
}
